<script>
import vueMixins from 'viralstyle-ui/src/vueMixins';

export default {
  mixins: [vueMixins],
  props: {
    currencyInfo: Object,
    campaign: Object,
    utmSource: String,
    affiliate: String,
    hidePrice: [String, Boolean],
    wrap: [String, Boolean],
    isWhitelabel: Boolean,
    threeColumns: [String, Boolean],
    isUserAdmin: Boolean,
    coupon: String,
  },
  data: () => ({
    disabled: false,
  }),
  computed: {
    uri() {
      let uri = this.campaign.uri || this.campaign.url;

      if (this.isWhitelabel) {
        let query = {};

        uri = uri.split('/');

        uri = uri[uri.length - 1];

        if (this.utmSource) {
          query.utm_source = this.utmSource;
        }

        if (this.coupon) {
          query.coupon = this.coupon;
        }

        return {
          name: 'sales',
          params: {
            campaignSlug: uri,
          },
          query,
        };
      } else {
        let params = [];

        if (this.utmSource) {
          params.push(`utm_source=${this.utmSource}`);
        }

        if (this.affiliate) {
          params.push(`aid=${this.affiliate}`);
        }

        if (this.coupon) {
          params.push(`coupon=${this.coupon}`);
        }

        return params.length ? `${uri}?${params.join('&')}` : uri;
      }
    },
    image() {
      if (this.campaign.default_side) {
        return this.campaign[`${this.campaign.default_side.toLowerCase()}_image`];
      }

      return this.campaign.preview_image || this.campaign.image;
    },
  },
  methods: {
    async hideCampaign(e) {
      e.stopPropagation();
      e.preventDefault();

      let { value } = swal.fire({
        type: 'question',
        showCancelButton: true,
        confirmButtonColor: config.ERROR_COLOR,
        title: `Hide ${this.campaign.name}?`,
        html: `
          <p>This action can only be undone in the admin dashboard.</p>
          <p class="mt-1 text-xs font-bold italic">This change will take several minutes to update and requires a manual page refresh.</p>
        `,
        confirmButtonText: 'Yes',
      });

      if (value) {
        this.$emit('hide-campaign', this.campaign);

        this.disabled = true;
      }
    },
  },
};
</script>

<template lang='pug'>
.inline-block.relative.select-none(
  :class='{\
    "w-20 mt-1_5 mb-1_5 md_mb-0": !wrap || isWhitelabel,\
    "max-w-lg py-2 px-1 product-group-product--container bg-white": wrap && !isWhitelabel,\
    "product-group-product--container-three-columns": wrap && threeColumns,\
  }',
)
  svg.h-1_5.w-1_5.top-1.right-1.absolute.cursor-pointer(
    v-if='isUserAdmin && !disabled',
    viewBox='0 0 24 24',
    taxindex='0',
    @click='hideCampaign',
    @keyup.enter='hideCampaign',
  )
    path.text-brand(fill='currentColor', d='M2,5.27L3.28,4L20,20.72L18.73,22L15.65,18.92C14.5,19.3 13.28,19.5 12,19.5C7,19.5 2.73,16.39 1,12C1.69,10.24 2.79,8.69 4.19,7.46L2,5.27M12,9A3,3 0 0,1 15,12C15,12.35 14.94,12.69 14.83,13L11,9.17C11.31,9.06 11.65,9 12,9M12,4.5C17,4.5 21.27,7.61 23,12C22.18,14.08 20.79,15.88 19,17.19L17.58,15.76C18.94,14.82 20.06,13.54 20.82,12C19.17,8.64 15.76,6.5 12,6.5C10.91,6.5 9.84,6.68 8.84,7L7.3,5.47C8.74,4.85 10.33,4.5 12,4.5M3.18,12C4.83,15.36 8.24,17.5 12,17.5C12.69,17.5 13.37,17.43 14,17.29L11.72,15C10.29,14.85 9.15,13.71 9,12.28L5.6,8.87C4.61,9.72 3.78,10.78 3.18,12Z')

  router-link.transition-250.transition-border.h-full.flex.flex-wrap.justify-center.items-center.bg-white(
    :class='{\
      "text-center": wrap,\
      "mx-_625 product-group-product": !wrap || isWhitelabel,\
    }',
    :to='uri',
  )
    img(
      :class='{\
        "w-16": wrap,\
        "pb-1 px-1 lg_px-2 pt-1 w-full": !wrap || isWhitelabel,\
      }',
      v-lazy='image',
      :alt='campaign.name',
    )
    template(v-if='wrap && !isWhitelabel')
      .text-xl.mt-1.break-anywhere.w-full {{campaign.name}}
      .text-xl.text-brand.mt-_25.w-full {{currency(campaign.selling_price || campaign.price, currencyInfo)}}
    template(v-else)
      .text-3xl.md_text-xl.text-brand-secondary.px-1.w-full.leading-none.break-words.whitespace-normal {{campaign.name}}
      .px-1.pb-1.w-full.mt-_5(v-if='!hidePrice')
        span.text-2xl.md_text-lg.text-brand.mr-_75 {{currency(campaign.selling_price || campaign.price, currencyInfo)}}
        span.text-xl.md_text-base.text-brand-secondary-300.line-through.font-light(v-if='campaign.msrp') {{currency(campaign.msrp, currencyInfo)}}
</template>

<style>
@import '~import/index';

.product-group-product {
  transition: box-shadow 250ms;
  border-radius: 4px;

  &:hover,
  &:active {
    box-shadow: 0 0 18px rgba(0, 0, 0, 0.2);
  }

  &--container {
    border-width: 1px;
    border-top-color: transparent;
    border-left-color: transparent;
    transition: all 200ms;

    &:nth-child(2n) {
      border-right-color: transparent;
    }

    @include mq(mf-md) {
      &:nth-child(2n) {
        border-right-color: #ebf0f7;
      }

      &:nth-child(3n) {
        border-right-color: transparent;
      }
    }

    @include mq(mf-lg) {
      &:nth-child(3n) {
        border-right-color: #ebf0f7;
      }

      &:nth-child(4n) {
        border-right-color: transparent;
      }
    }

    &-three-columns {
      @include mq(mf-lg) {
        &:nth-child(2n) {
          border-right-color: #ebf0f7;
        }

        &:nth-child(3n) {
          border-right-color: transparent;
        }
      }
    }

    &:hover,
    &:active {
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
      border-radius: 4px;
      transform: scale(1.015);
      border-color: #ebf0f7;
      z-index: 1;
    }
  }
}
</style>